import React, { useState, useEffect } from "react";
import "./css/common.css";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Container,
  alpha,
  useTheme,
} from "@mui/material";
import { LineChart, BarChart } from "@mui/x-charts";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import BusinessIcon from "@mui/icons-material/Business";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import BugReportIcon from "@mui/icons-material/BugReport";
import { motion, AnimatePresence } from "framer-motion";
import { post } from "../services/services";
import { ApiURL } from "../services/apiConstants";

function AdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [metricsData, setMetricsData] = useState({
    paidSubscriptions: 0,
    freeTrials: 0,
    demoRequests: 0,
    openBugs: 0,
  });
  const [recentDemoRequests, setRecentDemoRequests] = useState([]);
  const [lineChartData, setLineChartData] = useState({ free: [], paid: [], labels: [] });
  const [barChartData, setBarChartData] = useState({ countries: [], counts: [] });
  const theme = useTheme();

  // Helper to get last 6 months labels
  const getMonthLabels = () => {
    const labels = [];
    const date = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(date.getFullYear(), date.getMonth() - i, 1);
      labels.push(d.toLocaleString("default", { month: "short" }));
    }
    return labels;
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const formData = new FormData();
        const monthLabels = getMonthLabels();
        
        // Fetch All Required Data
        const [paidRes, trialRes, demoRes, bugRes, freeRegRes] = await Promise.all([
          post(ApiURL.paid_subscription_list, formData),
          post(ApiURL.free_trial_list, formData),
          post(ApiURL.demo_request_list, formData),
          post(ApiURL.company_bug_report_list_com, formData),
          post(ApiURL.free_registration_list, formData)
        ]);

        // Process Metrics
        const paidData = paidRes?.status ? (paidRes?.data || []) : [];
        const trialData = trialRes?.status ? (trialRes?.data || []) : [];
        const demoData = demoRes?.status ? (demoRes?.results || []) : [];
        const bugData = bugRes?.status ? (bugRes?.data || []) : [];
        const freeRegData = freeRegRes?.status ? (freeRegRes?.data || []) : [];

        setMetricsData({
          paidSubscriptions: paidData.length,
          freeTrials: trialData.length,
          demoRequests: demoRes?.total_records || 0,
          openBugs: bugData.length,
        });

        setRecentDemoRequests(demoData.slice(0, 5));

        // Process Line Chart Data (Last 6 Months)
        const freeCounts = Array(6).fill(0);
        const paidCounts = Array(6).fill(0);
        const now = new Date();

        const processList = (list, type) => {
          list.forEach(item => {
            const itemDate = new Date(item.created_at);
            const monthDiff = (now.getFullYear() - itemDate.getFullYear()) * 12 + (now.getMonth() - itemDate.getMonth());
            if (monthDiff >= 0 && monthDiff < 6) {
              const index = 5 - monthDiff;
              if (type === "free") freeCounts[index]++;
              else paidCounts[index]++;
            }
          });
        };

        processList(freeRegData, "free");
        processList(demoData, "free");
        processList(trialData, "free");
        processList(paidData, "paid");

        setLineChartData({ free: freeCounts, paid: paidCounts, labels: monthLabels });

        // Process Bar Chart Data (Country Distribution)
        const countryMap = {};
        [...freeRegData, ...demoData, ...trialData, ...paidData].forEach(item => {
          const country = item.country || "Unknown";
          countryMap[country] = (countryMap[country] || 0) + 1;
        });

        const sortedCountries = Object.entries(countryMap)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5);

        setBarChartData({
          countries: sortedCountries.map(c => c[0]),
          counts: sortedCountries.map(c => c[1])
        });

      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // metrics for dashboard
  const metrics = [
    {
      title: "Paid Subscriptions",
      value: metricsData.paidSubscriptions,
      icon: <TrendingUpIcon sx={{ fontSize: 32, color: "#fff" }} />,
      bgGradient: "linear-gradient(135deg, #05CD99 0%, #20c997 100%)",
      shadow: "0px 10px 20px rgba(5, 205, 153, 0.4)",
      growth: "Live",
      growthColor: "#05CD99",
    },
    {
      title: "Active Free Trials",
      value: metricsData.freeTrials,
      icon: <DirectionsBoatIcon sx={{ fontSize: 32, color: "#fff" }} />,
      bgGradient: "linear-gradient(135deg, #1e90ff 0%, #61dafb 100%)",
      shadow: "0px 10px 20px rgba(30, 144, 255, 0.4)",
      growth: "Live",
      growthColor: "#05CD99",
    },
    {
      title: "Demo Requests",
      value: metricsData.demoRequests,
      icon: <PeopleAltIcon sx={{ fontSize: 32, color: "#fff" }} />,
      bgGradient: "linear-gradient(135deg, #091e31 0%, #001529 100%)",
      shadow: "0px 10px 20px rgba(9, 30, 49, 0.4)",
      growth: "Pending",
      growthColor: "#FFB547",
    },
    {
      title: "Open Bugs",
      value: metricsData.openBugs,
      icon: <BugReportIcon sx={{ fontSize: 32, color: "#fff" }} />,
      bgGradient: "linear-gradient(135deg, #E11236 0%, #ff4d4d 100%)",
      shadow: "0px 10px 20px rgba(225, 18, 54, 0.4)",
      growth: "Reported",
      growthColor: "#EE5D50",
    },
  ];

  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    in: { opacity: 1, y: 0 },
    out: { opacity: 0, y: -20 },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        type: "spring",
        stiffness: 100,
      },
    }),
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: "linear-gradient(135deg, #F4F7FE 0%, #E2E8F0 100%)",
        }}
      >
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 2,
            ease: "easeInOut",
            times: [0, 0.5, 1],
            repeat: Infinity,
          }}
        >
          <DirectionsBoatIcon sx={{ fontSize: 60, color: "#E11236" }} />
        </motion.div>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        background: "linear-gradient(135deg, #F4F7FE 0%, #EEF2F6 100%)",
        minHeight: "100vh",
        p: { xs: 2, md: 4, lg: 5 },
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Decorative Background Elements using App Colors */}
      <Box
        sx={{
          position: "absolute",
          top: -100,
          right: -100,
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: "linear-gradient(135deg, rgba(225, 18, 54, 0.05) 0%, rgba(9, 30, 49, 0.05) 100%)",
          filter: "blur(60px)",
          zIndex: 0,
        }}
        component={motion.div}
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.5, 0.8, 0.5]
        }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -100,
          left: -100,
          width: 500,
          height: 500,
          borderRadius: "50%",
          background: "linear-gradient(135deg, rgba(181, 255, 241, 0.1) 0%, rgba(9, 30, 49, 0.05) 100%)",
          filter: "blur(80px)",
          zIndex: 0,
        }}
      />

      <Container maxWidth="xl" sx={{ p: "0 !important", position: "relative", zIndex: 1 }}>
        <motion.div
          initial="initial"
          animate="in"
          exit="out"
          variants={pageVariants}
          transition={{ duration: 0.6 }}
        >
          {/* Header with App Branding */}
          <Box sx={{ mb: 5, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
            <Box>
              <Typography
                variant="h3"
                fontWeight="900"
                sx={{
                  mb: 1,
                  background: "linear-gradient(90deg, #091e31 0%, #E11236 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  letterSpacing: "-1px",
                }}
              >
                Marine Admin Hub
              </Typography>
              <Typography variant="subtitle1" color="#68769F" fontWeight="500" sx={{ fontSize: "1.1rem" }}>
                System analytics and fleet operations overview.
              </Typography>
            </Box>
            <Box sx={{ display: { xs: "none", md: "block" } }}>
               <Chip 
                  icon={<DirectionsBoatIcon sx={{ color: "#091e31 !important" }} />}
                  label="Fleet Status: Optimal" 
                  sx={{ 
                    bgcolor: "rgba(181, 255, 241, 0.4)", 
                    color: "#091e31", 
                    fontWeight: "700",
                    border: "1px solid rgba(0,0,0,0.05)",
                    borderRadius: "12px",
                    px: 1
                  }} 
               />
            </Box>
          </Box>

          {/* Metrics Section using App Palette */}
          <Grid container spacing={4} sx={{ mb: 5 }}>
            {metrics.map((metric, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <motion.div
                  custom={index}
                  initial="hidden"
                  animate="visible"
                  variants={cardVariants}
                  whileHover={{ y: -8, scale: 1.02 }}
                >
                  <Card
                    sx={{
                      borderRadius: "24px",
                      background: "rgba(255, 255, 255, 0.8)",
                      backdropFilter: "blur(20px)",
                      border: "1px solid rgba(255, 255, 255, 0.5)",
                      boxShadow: "0px 20px 50px rgba(112, 144, 176, 0.08)",
                      overflow: "visible",
                    }}
                  >
                    <CardContent
                      sx={{
                        p: 3.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle2" color="#8F9BBA" fontWeight="600" textTransform="uppercase" letterSpacing="1px" sx={{ mb: 1 }}>
                          {metric.title}
                        </Typography>
                        <Typography variant="h4" color="#1B2559" fontWeight="800" sx={{ mb: 1, fontSize: "2rem" }}>
                          {metric.value}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Chip
                            label={metric.growth}
                            size="small"
                            sx={{
                              backgroundColor: alpha(metric.growthColor, 0.1),
                              color: metric.growthColor,
                              fontWeight: "800",
                              borderRadius: "8px",
                            }}
                          />
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: "20px",
                          background: metric.bgGradient,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          boxShadow: metric.shadow,
                          transform: "rotate(10deg)",
                          transition: "all 0.3s ease",
                          "&:hover": { transform: "rotate(0deg)" }
                        }}
                      >
                        {metric.icon}
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {/* Charts Row */}
          <Grid container spacing={4} sx={{ mb: 5 }}>
            <Grid item xs={12} md={7}>
              <motion.div
                 initial={{ opacity: 0, x: -30 }}
                 animate={{ opacity: 1, x: 0 }}
                 transition={{ delay: 0.4, duration: 0.6 }}
                 style={{ height: "100%",width:550 }}
              >
                <Card
                  sx={{
                    borderRadius: "24px",
                    background: "rgba(255, 255, 255, 0.8)",
                    backdropFilter: "blur(20px)",
                    boxShadow: "0px 20px 50px rgba(112, 144, 176, 0.08)",
                    border: "1px solid rgba(255, 255, 255, 0.6)",
                    height: "100%",
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                      <Typography variant="h6" fontWeight="800" color="#1B2559">
                        User Analytics
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2 }}>
                         <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#091e31" }} />
                            <Typography variant="caption" color="#68769F" fontWeight="600">Free/Demo</Typography>
                         </Box>
                         <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#E11236" }} />
                            <Typography variant="caption" color="#68769F" fontWeight="600">Paid</Typography>
                         </Box>
                      </Box>
                    </Box>
                    <Box sx={{ width: "100%", height: 350 }}>
                        <LineChart
                          xAxis={[{ 
                            data: lineChartData.labels, 
                            scaleType: "point",
                            disableTicks: true,
                          }]}
                          series={[
                            {
                              data: lineChartData.free,
                              curve: "monotoneX",
                              color: "#091e31",
                              area: true,
                              baseline: 'min',
                              showMark: true,
                            },
                            {
                              data: lineChartData.paid,
                              curve: "monotoneX",
                              color: "#E11236",
                              showMark: true,
                            }
                          ]}
                          height={350}
                          margin={{ top: 20, bottom: 40, left: 40, right: 20 }}
                          sx={{
                            '& .MuiAreaElement-root': { fill: 'url(#lineGradient)', fillOpacity: 0.1 },
                            '& .MuiLineElement-root': { strokeWidth: 3 },
                          }}
                        >
                           <defs>
                              <linearGradient id="lineGradient" x1="0" x2="0" y1="0" y2="1">
                                <stop offset="0%" stopColor="#091e31" stopOpacity={0.4} />
                                <stop offset="100%" stopColor="#091e31" stopOpacity={0} />
                              </linearGradient>
                           </defs>
                        </LineChart>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={5}>
              <motion.div
                 initial={{ opacity: 0, x: 30 }}
                 animate={{ opacity: 1, x: 0 }}
                 transition={{ delay: 0.5, duration: 0.6 }}
                 style={{ height: "100%",width:550  }}
              >
                <Card
                  sx={{
                    borderRadius: "24px",
                    background: "rgba(255, 255, 255, 0.8)",
                    backdropFilter: "blur(20px)",
                    boxShadow: "0px 20px 50px rgba(112, 144, 176, 0.08)",
                    border: "1px solid rgba(255, 255, 255, 0.6)",
                    height: "100%",
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h6" fontWeight="800" color="#1B2559" sx={{ mb: 3 }}>
                      Top Adoption Countries
                    </Typography>
                    <Box sx={{ height: 350 }}>
                      <BarChart
                        xAxis={[{ scaleType: "band", data: barChartData.countries, disableTicks: true }]}
                        series={[
                          { 
                            data: barChartData.counts, 
                            color: "#091e31", 
                            label: "Total Registrations",
                          }
                        ]}
                        height={350}
                        margin={{ top: 20, bottom: 40, left: 40, right: 10 }}
                        borderRadius={8}
                        sx={{
                          '& .MuiBarElement-root': { fill: 'url(#barGradient)' }
                        }}
                      >
                         <defs>
                            <linearGradient id="barGradient" x1="0" x2="0" y1="0" y2="1">
                              <stop offset="0%" stopColor="#091e31" />
                              <stop offset="100%" stopColor="#1e90ff" />
                            </linearGradient>
                         </defs>
                      </BarChart>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>

          {/* Activity Section */}
          <motion.div
             initial={{ opacity: 0, y: 30 }}
             animate={{ opacity: 1, y: 0 }}
             transition={{ delay: 0.6, duration: 0.6 }}
          >
            <Card
              sx={{
                borderRadius: "24px",
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(20px)",
                boxShadow: "0px 20px 50px rgba(112, 144, 176, 0.08)",
                border: "1px solid rgba(255, 255, 255, 0.8)",
              }}
            >
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ p: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                   <Typography variant="h6" fontWeight="800" color="#1B2559">
                    Recent Demo Flux
                  </Typography>
                  <Chip 
                    label="View All Inquiries" 
                    clickable 
                    sx={{ bgcolor: "rgba(225, 18, 54, 0.1)", color: "#E11236", fontWeight: "700", border: "1px solid rgba(225, 18, 54, 0.2)" }} 
                  />
                </Box>
                
                <TableContainer>
                  <Table sx={{ minWidth: 700 }}>
                    <TableHead sx={{ backgroundColor: "#091e31" }}>
                      <TableRow>
                        {["CLIENT NAME", "EMAIL", "COMPANY", "STATUS", "DATE"].map((head) => (
                          <TableCell
                            key={head}
                            sx={{ color: "#fff", fontWeight: "700", py: 2, fontSize: "0.85rem", letterSpacing: "0.5px" }}
                          >
                            {head}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentDemoRequests.map((row, index) => (
                        <TableRow
                          key={row.id}
                          component={motion.tr}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.7 + (index * 0.1) }}
                          sx={{ 
                            "&:last-child td, &:last-child th": { border: 0 },
                            transition: "background-color 0.2s ease",
                            "&:hover": { backgroundColor: "#F8FAFC" }
                          }}
                        >
                          <TableCell sx={{ py: 2.5 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                              <Avatar sx={{ bgcolor: "#091e31", width: 40, height: 40, fontWeight: "700" }}>
                                {row.name?.charAt(0)}
                              </Avatar>
                              <Typography variant="body2" sx={{ color: "#1B2559", fontWeight: "700" }}>
                                {row.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ color: "#68769F", fontWeight: "600" }}>{row.email}</TableCell>
                          <TableCell sx={{ color: "#68769F", fontWeight: "600" }}>{row.company_name}</TableCell>
                          <TableCell>
                            <Chip
                              label={row.all_status || "Incoming"}
                              size="small"
                              sx={{
                                backgroundColor: alpha("#1e90ff", 0.1),
                                color: "#1e90ff",
                                fontWeight: "800",
                                borderRadius: "8px",
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: "#1B2559", fontWeight: "600" }}>{new Date(row.created_at).toLocaleDateString()}</TableCell>
                        </TableRow>
                      ))}
                      {recentDemoRequests.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 4, color: "#8F9BBA", fontWeight: "600" }}>
                            No recent demo requests detected.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
}

export default AdminDashboard;

